import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnInit,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs';

import {
  IAppointment,
  IDoctors,
  TimeSlot,
  AppointmentStatus,
  DaysOfWeek,
  MajorCategories,
  IBodyScanDoctors,
} from '@alpha/types';
import { Button, AppLogger, Util } from 'app-core';
import {
  ClinicAppointmentService,
  AssetsRegistrationService,
  BodyScanAppointmentService,
  RatingService,
} from 'app-service';
import { DialogService } from 'app-shared-ui';
import { AppointmentScheduleCommon } from './appointment-schedule.common';

import ButtonType = Data.Core.ButtonType;
import { formatDate } from '@angular/common';
import { environment } from 'src/environments/environment.prod';

@Component({
  selector: 'app-appointment-schedule',
  templateUrl: './appointment-schedule.component.html',
  styleUrls: ['./appointment-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [BodyScanAppointmentService, DialogService, RatingService],
})
export class AppointmentScheduleComponent
  extends AppointmentScheduleCommon
  implements OnInit
{
  public environment = environment;
  public bookAppointmentButton: Button = new Button({
    label: 'Book Appointment',
    type: ButtonType.button,
    action: () => {
      if (this.categoryId === MajorCategories.Doctor) {
        this._bookClinicAppointment();
      }

      if (this.categoryId === MajorCategories.BodyScan) {
        this._bookBodyScanClinicAppointment();
      }
    },
    disabled: () => {
      if (!this.timeControl.value || !this.timeControl.dirty) {
        return true;
      } else if (this.appointmentForm?.invalid) {
        return true;
      } else {
        return false;
      }
    },
  });
  public removeAppointmentButton: Button = new Button({
    label: 'Cancel Appointment',
    type: ButtonType.button,
    action: () => this._removeClinicAppointment(this.appointmentId),
  });
  public okeyButton: Button = new Button({
    label: 'OK',
    type: ButtonType.button,
    action: () => {
      this._route.navigate(['/appointment'], {
        queryParamsHandling: 'preserve',
      });
      this._ngZone.run(() => {
        this._dialogService.closeDialog();
      });
    },
  });
  public ratingOkeyButton: Button = new Button({
    label: 'OK',
    type: ButtonType.button,
    action: () => {
      this._ngZone.run(() => {
        this._dialogService.closeDialog();
      });
    },
  });

  public constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _bodyScanAppointmentService: BodyScanAppointmentService,
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _iconService: AssetsRegistrationService,
    private readonly _route: Router,
    private readonly _formBuilder: FormBuilder,
    private readonly _appointmentService: ClinicAppointmentService,
    private readonly _dialogService: DialogService,
    private readonly _ratingService: RatingService,
    private readonly _ngZone: NgZone,
  ) {
    super();
    this.icon = this._iconService.getIconURL('medical-ico');
  }

  public ngOnInit(): void {
    if (this._route.url.split('/')[2] === 'view') {
      this.doctorId = this._route.url.split('/')[4];
      this.clinicId = this._route.url.split('/')[5];
      this.appointmentId = this._route.url.split('/')[6].split('?')[0];
      this.editMode = false;

      // Extract existing query params
      if (
        Number(this._activatedRoute.snapshot.queryParamMap.get('acf')) ===
        Number(MajorCategories.Doctor)
      ) {
        this._appointmentService
          .findAppointmentById(this.appointmentId)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((appointment: Partial<IAppointment>) => {
            this.item.userId = appointment!.doctorId!.userId;
            this.item.clinic = appointment!.doctorId!.clinic;
            this.item.qualification = appointment!.doctorId!.qualification;
            this.item.date = appointment.date?.toString().split('T')[0]
              ? formatDate(
                  new Date(appointment.date?.toString().split('T')[0]),
                  'EEEE, MMMM d, y',
                  'en-US',
                )
              : '';
            this.item.time = TimeSlot[appointment.time || 0];
            this.item.status = appointment.status;
            if (
              this.item.status === AppointmentStatus.NotCompleted ||
              this.item.status === AppointmentStatus.Rejected ||
              this.item.status === AppointmentStatus.Unavailable ||
              this.item.status === AppointmentStatus.Pending
            ) {
              this.showRating = false;
            } else {
              this.showRating = true;
            }
            this.showTimeControl = true;
            this.showRemoveAppointmentBtn =
              appointment.status === AppointmentStatus.Pending ? true : false;
            if (appointment && appointment.rating) {
              this.ratings[appointment.rating - 1].selected = true;
              this._cdRef?.markForCheck();
            }
            this._cdRef.markForCheck();
          });
      }

      if (
        Number(this._activatedRoute.snapshot.queryParamMap.get('acf')) ===
        Number(MajorCategories.BodyScan)
      ) {
        this._bodyScanAppointmentService
          .findAppointmentById(this.appointmentId)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((appointment: Partial<IAppointment>) => {
            this.item.userId = appointment!.doctorId!.userId;
            this.item.clinic = appointment!.doctorId!.clinic;
            this.item.qualification = appointment!.doctorId!.qualification;
            this.item.date = appointment.date?.toString().split('T')[0]
              ? formatDate(
                  new Date(appointment.date?.toString().split('T')[0]),
                  'EEEE, MMMM d, y',
                  'en-US',
                )
              : '';
            this.item.time = TimeSlot[appointment.time || 0];
            this.item.status = appointment.status;
            if (
              this.item.status === AppointmentStatus.NotCompleted ||
              this.item.status === AppointmentStatus.Rejected ||
              this.item.status === AppointmentStatus.Unavailable ||
              this.item.status === AppointmentStatus.Pending
            ) {
              this.showRating = false;
            } else {
              this.showRating = true;
            }
            this.showTimeControl = true;
            this.showRemoveAppointmentBtn =
              appointment.status === AppointmentStatus.Pending ? true : false;
            if (appointment && appointment.rating) {
              this.ratings[appointment.rating - 1].selected = true;
              this._cdRef?.markForCheck();
            }
            this._cdRef.markForCheck();
          });
      }
    } else {
      const route = this._route.url.split('/');
      this.doctorId = route[3];
      this.clinicId = route[4].split('?')[0];
      const url = new URL(this._route.url, environment.baseURL);
      this.categoryId = Number(url.searchParams.get('c'));
      this.editMode = true;
      // Extract existing query params
      if (this.categoryId === MajorCategories.Doctor) {
        this._appointmentService
          .getAppointmentProfile(this.doctorId, this.clinicId)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((appointment: Partial<IDoctors>) => {
            this.item = appointment;
            this.holidaysList =
              (appointment.clinic &&
                appointment.clinic[0] &&
                appointment.clinic[0]!.appointment?.holiday) ||
              [];
            this._cdRef.markForCheck();
          });
      }

      if (this.categoryId === MajorCategories.BodyScan) {
        this._bodyScanAppointmentService
          .getAppointmentProfile(this.doctorId, this.clinicId)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((appointment: Partial<IBodyScanDoctors>) => {
            this.item = appointment;
            this.holidaysList =
              (appointment.clinic &&
                appointment.clinic[0] &&
                appointment.clinic[0]!.appointment?.holiday) ||
              [];
            this._cdRef.markForCheck();
          });
      }
    }

    this.loadForms();

    this.ratings = [
      {
        text: 1,
        selected: false,
      },
      {
        text: 2,
        selected: false,
      },
      {
        text: 3,
        selected: false,
      },
      {
        text: 4,
        selected: false,
      },
      {
        text: 5,
        selected: false,
      },
      {
        text: 6,
        selected: false,
      },
      {
        text: 7,
        selected: false,
      },
      {
        text: 8,
        selected: false,
      },
      {
        text: 9,
        selected: false,
      },
      {
        text: 10,
        selected: false,
      },
    ];

    this._cdRef.markForCheck();
  }

  public onDateChanege(selectedDate: Date | null): void {
    this.selectedDate = selectedDate;

    // Check if selected date is holiday
    const isHoliday = Util.isSameDateInArray(
      this.selectedDate!.toString(),
      this.holidaysList.map((date) => date.toString()),
    );

    // Check if selected date is fall under working days
    const isWorkingDay = this.item.clinic[0]!.appointment!.workDays.some(
      (workDay: {
        day: DaysOfWeek;
        time: Array<{
          from: TimeSlot;
          to: TimeSlot;
        }>;
      }) =>
        workDay.day === Util.getDayOfWeekEnum(this.selectedDate!.toString()) &&
        workDay.time.length > 0,
    );

    if (isHoliday) {
      this.showTimeControl = false;
      this._dialogService.openDialog({ body: this.holidayErrorMsg });
    } else if (!isWorkingDay) {
      this.showTimeControl = false;
      this._dialogService.openDialog({ body: this.workingDayErrorMsg });
    } else {
      this.showTimeControl = true;

      if (this.categoryId === MajorCategories.Doctor) {
        // Get appointment time slots
        this._getClinicAppointmentTimeSlots();
      }

      if (this.categoryId === MajorCategories.BodyScan) {
        this._getBodyScanClinicAppointmentTimeSlots();
      }
    }
  }

  public getCalculatedExperience(year: number): number {
    return Util.calculateExperience(year);
  }

  public selectRating(selectedIndex: number): void {
    this.ratings.forEach((rating, index) => {
      rating.selected = index === selectedIndex ? true : false;
      this._cdRef.markForCheck();
    });

    if (
      Number(this._activatedRoute.snapshot.queryParamMap.get('acf')) ===
      Number(MajorCategories.Doctor)
    ) {
      this._addClinicRatings(
        this.ratings[selectedIndex].text,
        this.appointmentId,
      );
    }

    if (
      Number(this._activatedRoute.snapshot.queryParamMap.get('acf')) ===
      Number(MajorCategories.BodyScan)
    ) {
      this._addBodyScanRatings(
        this.ratings[selectedIndex].text,
        this.appointmentId,
      );
    }
  }

  private _addClinicRatings(rating: number, appointmentId: string): void {
    this._ratingService
      .createClinicRating({
        rating: rating,
        appointmentId: appointmentId,
      })
      .pipe()
      .subscribe(
        () => {
          this._dialogService.openDialog({
            header: this.successMsgHeader,
            body: this.ratingSuccessMsg,
            footer: this.ratingFooterMsg,
          });
        },
        (error) => {
          this._dialogService.openDialog({ body: this.saveRatingErrorMsg });
          AppLogger.Error(error);
        },
      );
  }

  private _addBodyScanRatings(rating: number, appointmentId: string): void {
    this._ratingService
      .createBodyScanRating({
        rating: rating,
        appointmentId: appointmentId,
      })
      .pipe()
      .subscribe(
        () => {
          this._dialogService.openDialog({
            header: this.successMsgHeader,
            body: this.ratingSuccessMsg,
            footer: this.ratingFooterMsg,
          });
        },
        (error) => {
          this._dialogService.openDialog({ body: this.saveRatingErrorMsg });
          AppLogger.Error(error);
        },
      );
  }

  private loadForms(): void {
    this.appointmentForm = this._formBuilder?.group([
      this.dateControl,
      this.timeControl,
    ]);
    this._cdRef?.markForCheck();
  }

  private _bookClinicAppointment(): void {
    if (this.selectedDate) {
      var d = new Date(this.selectedDate);
      var convertedDate = new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
        d.getHours(),
        d.getMinutes() - d.getTimezoneOffset(),
      ).toISOString();
      const req = {
        _id: this.doctorId,
        date: new Date(convertedDate),
        time: this.appointmentForm?.controls[1]!.value,
        clinicId: {
          _id: this.clinicId,
        },
        doctorId: {
          _id: this.doctorId,
        },
      };

      this._appointmentService
        .createAppointment(req)
        .pipe()
        .subscribe(
          (next) => {
            this._dialogService.openDialog({
              header: this.successMsgHeader,
              body: this.successMsg,
              footer: this.footerMsg,
            });
          },
          (error) => {
            if (error.error === 'HttpException: Duplicate Record') {
              this._dialogService.openDialog({ body: this.errorMsg });
              this.appointmentForm?.reset();
            }
            if (error.error === 'HttpException: Time Slot Full') {
              this._dialogService.openDialog({
                body: this.appointmentFullErrorMsg,
              });
              this.appointmentForm?.reset();
            }
            AppLogger.Error(error);
          },
        );
    }
  }

  private _bookBodyScanClinicAppointment(): void {
    if (this.selectedDate) {
      var d = new Date(this.selectedDate);
      var convertedDate = new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
        d.getHours(),
        d.getMinutes() - d.getTimezoneOffset(),
      ).toISOString();
      const req = {
        _id: this.doctorId,
        date: new Date(convertedDate),
        time: this.appointmentForm?.controls[1]!.value,
        clinicId: {
          _id: this.clinicId,
        },
        doctorId: {
          _id: this.doctorId,
        },
      };

      this._bodyScanAppointmentService
        .createAppointment(req)
        .pipe()
        .subscribe(
          (next) => {
            this._dialogService.openDialog({
              header: this.successMsgHeader,
              body: this.successMsg,
              footer: this.footerMsg,
            });
          },
          (error) => {
            if (error.error === 'HttpException: Duplicate Record') {
              this._dialogService.openDialog({ body: this.errorMsg });
              this.appointmentForm?.reset();
            }
            if (error.error === 'HttpException: Time Slot Full') {
              this._dialogService.openDialog({
                body: this.appointmentFullErrorMsg,
              });
              this.appointmentForm?.reset();
            }
            AppLogger.Error(error);
          },
        );
    }
  }

  private _removeClinicAppointment(appointmentId: string): void {
    this._appointmentService
      .removeAppointment(appointmentId)
      .pipe()
      .subscribe(
        (next) => {
          this._dialogService.openDialog({
            header: this.successMsgHeader,
            body: this.removeAppointmentMsg,
            footer: this.footerMsg,
          });
        },
        (error) => {
          if (error.status === 409) {
            this._dialogService.openDialog({
              body: this.removeAppointmentErrorMsg,
            });
            this.appointmentForm?.reset();
          }
          AppLogger.Error(error);
        },
      );
  }

  private _getClinicAppointmentTimeSlots(): void {
    this._appointmentService
      .getAppointmentTimeSlots(this.selectedDate || new Date(), this.clinicId)
      .pipe()
      .subscribe(
        (next) => {
          // Get day of selected date
          const selectedDay = Util.getDayOfWeekEnum(
            this.selectedDate!.toString(),
          );
          const selectedTimeSlot = next.timings.filter(
            (day) => day.day === selectedDay,
          );

          if (selectedTimeSlot.length > 0) {
            // Compare selected day with response & populate timings
            this.timeSlot = selectedTimeSlot[0]!.time!.map((timeSlot) => {
              return {
                disabled: false,
                selected: false,
                displayText: TimeSlot[timeSlot],
                value: timeSlot,
              };
            });
          }
        },
        (error) => {
          AppLogger.Error(error);
        },
        () => this._cdRef.markForCheck(),
      );
  }

  private _getBodyScanClinicAppointmentTimeSlots(): void {
    this._bodyScanAppointmentService
      .getBodyScanAppointmentTimeSlots(
        this.selectedDate || new Date(),
        this.clinicId,
      )
      .pipe()
      .subscribe(
        (next) => {
          // Get day of selected date
          const selectedDay = Util.getDayOfWeekEnum(
            this.selectedDate!.toString(),
          );
          const selectedTimeSlot = next.timings.filter(
            (day) => day.day === selectedDay,
          );

          if (selectedTimeSlot.length > 0) {
            // Compare selected day with response & populate timings
            this.timeSlot = selectedTimeSlot[0]!.time!.map((timeSlot) => {
              return {
                disabled: false,
                selected: false,
                displayText: TimeSlot[timeSlot],
                value: timeSlot,
              };
            });
          }
        },
        (error) => {
          AppLogger.Error(error);
        },
        () => this._cdRef.markForCheck(),
      );
  }
}
